import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", target: "top" },
  { label: "About", target: "about" },
  { label: "Services", target: "services" },
  { label: "Approach", target: "approach" },
  { label: "Contact", target: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(target), 200);
    } else {
      scrollTo(target);
    }
  };

  const scrollTo = (target) => {
    if (target === "top") {
      window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0);
      return;
    }
    const el = document.getElementById(target);
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView();
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="font-display text-2xl tracking-[0.25em] font-semibold"
        >
          LEYLAK<span className="text-accent">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-${l.target}`}
              onClick={() => go(l.target)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors tracking-wide relative group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="rounded-full border border-accent/60 text-accent px-5 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Start a project
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-foreground"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-md border-b border-white/10 px-4 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-mobile-${l.target}`}
              onClick={() => go(l.target)}
              className="text-left text-lg text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/admin/login"
            className="text-left text-sm text-accent mt-2"
            data-testid="nav-mobile-admin"
          >
            Admin
          </Link>
        </div>
      )}
    </header>
  );
}
